export type CrisisSeverity = "none" | "low_concern" | "crisis_support" | "urgent_crisis";

/**
 * PRIVACY & SAFETY WARNING (CCPA/GDPR COMPLIANT):
 * Crisis-related search texts and distress queries are highly sensitive, protected personal information.
 * Under standard legal and ethical guidelines, storing, transmitting, or exposing raw self-harm or suicidal
 * query strings is strictly prohibited to prevent:
 *   1. Direct user re-identification or tracing of mental health conditions.
 *   2. Accidental disclosure in administrative logs, telemetry dashboards, or third-party paid AI integrations.
 *   3. Secondary distress or psychological profiling.
 * 
 * To preserve privacy, this system uses deterministic local keyword parsing to map intent to severity level.
 * High-severity queries must ALWAYS be redacted/obfuscated (e.g., replaced with generic "Suicide Prevention & Support Resource Search" labels)
 * before persisting to any cloud analytics database, local storage logs, or administrative audit streams.
 */

export interface CrisisDetectionResult {
  severity: CrisisSeverity;
  matchedTerms: string[];
  message: string;
  shouldBlock: boolean;
  safeLogCode?: string;
}

// 1. URGENT CRISIS PHRASES (Direct intent, plans, methods, active self-harm, overdose, bleeding)
const URGENT_PHRASES = [
  "kill myself",
  "kill my self",
  "going to kill myself",
  "going to kill my self",
  "im going to kill myself",
  "i am going to kill myself",
  "kill myself tonight",
  "end my life tonight",
  "plan to kill myself",
  "plan to kill my self",
  "how to kill myself",
  "how to kill my self",
  "best way to kill myself",
  "best way to kill my self",
  "painless way to die",
  "fastest way to die",
  "i took pills",
  "took pills to die",
  "i overdosed",
  "overdosed",
  "want to overdose",
  "going to overdose",
  "want to hang myself",
  "going to hang myself",
  "want to shoot myself",
  "going to shoot myself",
  "want to jump off",
  "going to jump off",
  "going to jump",
  "want to cut my wrists",
  "bleeding on purpose",
  "took too many pills",
  "someone took too many pills",
  "i drank bleach",
  "drank bleach",
  "swallowed poison",
  "want to poison myself",
  "going to poison myself",
  "cut too deep",
  "can not stop bleeding",
  "cant stop bleeding",
  "tried to kill myself",
  "suicide attempt",
  "kms",
  "k m s"
];

// 2. CRISIS SUPPORT PHRASES (Direct or indirect ideation, help for someone else, self-harm thoughts)
const CRISIS_SUPPORT_PHRASES = [
  "want to die",
  "wanna die",
  "wana die",
  "want death",
  "i want death",
  "wish i was dead",
  "should be dead",
  "need to die",
  "do not want to live",
  "want to end it",
  "end it all",
  "end my life",
  "take my own life",
  "take my life",
  "suicidal",
  "feel suicidal",
  "thinking about suicide",
  "suicide thoughts",
  "suicidal thoughts",
  "unalive myself",
  "un alive myself",
  "delete myself",
  "delete my life",
  "off myself",
  "sewer slide",
  "suicde",
  "suicidal ideation",
  "wanna kms",
  "gonna kms",
  
  // Indirect distress / Hopelessness
  "can not do this anymore",
  "cant do this anymore",
  "can not go on",
  "cant go on",
  "no reason to live",
  "nothing to live for",
  "everyone would be better without me",
  "everyone would be better off without me",
  "better off without me",
  "want to disappear forever",
  "do not want to exist",
  "dont want to exist",
  "wish i never existed",
  "done with life",
  "life is pointless",
  "give up on life",

  // Self-harm general
  "want to hurt myself",
  "hurt myself",
  "self harm",
  "self-harm",
  "cut myself",
  "cutting myself",
  "want to cut",
  "burn myself",
  "hit myself",
  "make myself bleed",
  "bleed out",
  "relapsed self harm",
  "want pain",
  "deserve pain",

  // Help for someone else
  "friend wants to kill himself",
  "friend wants to kill herself",
  "friend wants to die",
  "friend is suicidal",
  "brother wants to kill himself",
  "sister wants to kill herself",
  "kid wants to die",
  "someone said they want to die",
  "what do i do if someone is suicidal",
  "how to help suicidal friend",
  "how to help my suicidal friend",

  // Generic crisis help searches
  "suicide help",
  "crisis help",
  "self harm help",
  "depression help",
  "mental health crisis",
  "thoughts of suicide",
  "i feel unsafe",
  "help me not hurt myself",
  "help me stay alive",
  "scared i might hurt myself",
  "988",
  "suicide hotline",
  "crisis hotline",
  "lifeline",
  "emergency mental health"
];

// 3. LOW CONCERN PHRASES (Vague distress terms without explicit suicidal/self-harm ideation)
const LOW_CONCERN_PHRASES = [
  "feel so down",
  "feel depressed",
  "i am sad",
  "extremely sad",
  "i am lonely",
  "hate my life",
  "i am hopeless",
  "nobody loves me",
  "have no friends",
  "life is hard",
  "depressed",
  "depression",
  "crying all day",
  "anxiety attack",
  "panic attack",
  "want to cry",
  "can not stop crying"
];

// 4. FALSE POSITIVE PATTERNS
const FALSE_POSITIVES = [
  "death metal",
  "death stranding",
  "death note",
  "death in literature",
  "death mean",
  "death counter",
  "movie quote",
  "character wants to die",
  "sad character",
  "kill process",
  "kill server",
  "kill port",
  "plants dying",
  "battery died",
  "dead pixel",
  "dead link",
  "dead battery",
  "dead end",
  "kill weeds",
  "dying hair",
  "dying clothes",
  "kill switch",
  "kill bill",
  "left 4 dead",
  "red dead",
  "die hard",
  "die casting",
  "kill a thread",
  "kill thread",
  "kill task",
  "kill command",
  "kill execution",
  "sudden death",
  "in a book",
  "write a sad character",
  "how to write a sad character"
];

const JOB_FALSE_POSITIVES = [
  "job",
  "jobs",
  "hiring",
  "counselor",
  "counselors",
  "opening",
  "openings",
  "clinic",
  "clinics",
  "therapist",
  "therapists",
  "hospital",
  "hospitals",
  "services",
  "prevention",
  "counseling",
  "volunteer",
  "volunteers"
];

/**
 * Normalizes input text for safety search checks.
 * Handles casing, extra spaces, spacing variations, repeated letters, common misspellings, and contractions.
 */
export function normalizeText(text: string): { normalized: string; noSpaces: string } {
  if (!text) return { normalized: "", noSpaces: "" };
  
  // 1. Convert to lowercase and trim
  let clean = text.toLowerCase().trim();
  
  // 2. Collapse repeated characters to prevent evasion (e.g. "kkkiiillll" -> "kill")
  // Collapse 3 or more consecutive identical characters to 1, or 2 if standard (like 'll' or 'ss')
  clean = clean.replace(/(.)\1{2,}/g, "$1$1");

  // 3. Replace curly apostrophes with straight ones
  clean = clean.replace(/[’‘]/g, "'");

  // 4. Substitute common contractions/slangs/misspellings using word boundaries
  const wordReplacements: { [key: string]: string } = {
    "i'm": "i am",
    "im": "i am",
    "cant": "can not",
    "can't": "can not",
    "cannot": "can not",
    "dont": "do not",
    "don't": "do not",
    "wanna": "want to",
    "wana": "want to",
    "gonna": "going to",
    "suicde": "suicide",
    "suicid": "suicide",
    "sewer slide": "suicide",
    "sewerslide": "suicide",
    "un alive": "unalive",
    "un-alive": "unalive",
    "self-harm": "self harm"
  };

  for (const [key, replacement] of Object.entries(wordReplacements)) {
    const escapedKey = key.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");
    const regex = new RegExp(`\\b${escapedKey}\\b`, "g");
    clean = clean.replace(regex, replacement);
  }

  // 5. Replace punctuation/symbols with spaces, then collapse multiple spaces to a single space
  let normalized = clean
    .replace(/[^a-z0-9]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  // Run replacements again in case punctuation was stripped/separated
  for (const [key, replacement] of Object.entries(wordReplacements)) {
    const keyClean = key.replace(/[^a-z0-9]/g, " ");
    if (keyClean !== key && keyClean.trim() !== "") {
      const escapedKeyClean = keyClean.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");
      const regex = new RegExp(`\\b${escapedKeyClean}\\b`, "g");
      normalized = normalized.replace(regex, replacement);
    }
  }
  normalized = normalized.replace(/\s+/g, " ").trim();

  // Strip all whitespaces to catch spaced phrases (e.g., "k m s" -> "kms")
  const noSpaces = normalized.replace(/\s+/g, "");

  return { normalized, noSpaces };
}

/**
 * Checks if 'kys' is used in a self-harm/crisis context towards self.
 */
function isKysCrisisContext(normalized: string): boolean {
  if (!/\bkys\b/.test(normalized)) return false;
  
  // Context signals showing user is thinking of 'kys' towards themselves or asking about it in crisis
  const selfContextWords = ["i", "im", "gonna", "wanna", "want", "should", "myself", "me", "kms"];
  return selfContextWords.some(word => {
    const regex = new RegExp(`\\b${word}\\b`);
    return regex.test(normalized);
  });
}

export function detectCrisisSearch(input: string): CrisisDetectionResult {
  if (!input) {
    return { severity: "none", matchedTerms: [], message: "", shouldBlock: false };
  }

  const { normalized, noSpaces } = normalizeText(input);

  // 1. Match against URGENT categories
  const matchedUrgent: string[] = [];
  for (const phrase of URGENT_PHRASES) {
    const { normalized: pNorm, noSpaces: pNoSpaces } = normalizeText(phrase);
    
    // Check if the query contains this specific phrase
    if (
      normalized.includes(pNorm) || 
      (pNoSpaces.length > 2 && noSpaces.includes(pNoSpaces)) || 
      (pNorm === "kms" && noSpaces === "kms")
    ) {
      matchedUrgent.push(phrase);
    }
  }

  // Handle special 'sui' whole word match (sui is high concern, let's treat it as crisis_support)
  const hasSuiWord = /\bsui\b/.test(normalized);
  const matchedSupport: string[] = [];
  
  if (hasSuiWord) {
    matchedSupport.push("sui");
  }

  // Handle 'kys' in crisis context
  const hasKysContext = isKysCrisisContext(normalized);
  if (hasKysContext) {
    matchedSupport.push("kys (crisis context)");
  }

  // 2. Match against CRISIS SUPPORT categories
  for (const phrase of CRISIS_SUPPORT_PHRASES) {
    const { normalized: pNorm, noSpaces: pNoSpaces } = normalizeText(phrase);
    if (normalized.includes(pNorm) || (pNoSpaces.length > 2 && noSpaces.includes(pNoSpaces))) {
      matchedSupport.push(phrase);
    }
  }

  // 3. Match against LOW CONCERN categories
  const matchedLow: string[] = [];
  for (const phrase of LOW_CONCERN_PHRASES) {
    const { normalized: pNorm } = normalizeText(phrase);
    if (normalized.includes(pNorm)) {
      matchedLow.push(phrase);
    }
  }

  // Check if we have any matches
  const hasUrgentMatches = matchedUrgent.length > 0;
  const hasSupportMatches = matchedSupport.length > 0;
  const hasLowMatches = matchedLow.length > 0;

  if (!hasUrgentMatches && !hasSupportMatches && !hasLowMatches) {
    return { severity: "none", matchedTerms: [], message: "", shouldBlock: false };
  }

  // Determine highest initial severity
  let initialSeverity: CrisisSeverity = "none";
  let matchedTerms: string[] = [];

  if (hasUrgentMatches) {
    initialSeverity = "urgent_crisis";
    matchedTerms = matchedUrgent;
  } else if (hasSupportMatches) {
    initialSeverity = "crisis_support";
    matchedTerms = matchedSupport;
  } else if (hasLowMatches) {
    initialSeverity = "low_concern";
    matchedTerms = matchedLow;
  }

  // 4. FALSE POSITIVE CHECK
  // Check if any false positive pattern is found in the normalized input
  const matchedFalsePositive = FALSE_POSITIVES.find(fp => normalized.includes(fp));
  
  if (matchedFalsePositive) {
    // If a false positive pattern is found, we should override and suppress the crisis detection
    // EXCEPT when there is a highly explicit personal urgent phrase present (e.g., "kill myself", "took too many pills", "overdosed")
    const isExplicitActiveIntent = matchedUrgent.some(term => 
      term.includes("kill myself") || 
      term.includes("kill my self") ||
      term === "kms" || 
      term.includes("overdose") ||
      term.includes("took too many pills") ||
      term.includes("bleed out") ||
      term.includes("poison myself")
    );

    if (!isExplicitActiveIntent) {
      // Comments explaining why crisis text is sensitive and false positives are suppressed:
      // Overriding to 'none' prevents unnecessary crisis UI triggers for educational, entertainment, or system administrator tasks.
      return { severity: "none", matchedTerms: [], message: "", shouldBlock: false };
    }
  }

  // 5. JOB-SEEKING / COUNSELOR FALSE POSITIVE CHECK
  const containsJobKeywords = JOB_FALSE_POSITIVES.some(jobWord => normalized.includes(jobWord));
  if (containsJobKeywords) {
    // If it contains counseling/hospital job search terms, downgrade severity to prevent blocking.
    // However, if there is a highly explicit active suicide statement, keep it as crisis_support instead of blocking.
    const isExplicitActiveIntent = matchedUrgent.some(term => 
      term.includes("kill myself") || 
      term.includes("kill my self") ||
      term === "kms"
    );

    if (!isExplicitActiveIntent) {
      return {
        severity: "crisis_support",
        matchedTerms,
        message: "Looking for mental health, emergency services, or counseling jobs? Help and resources are always available. If you or someone you know needs support, call or text 988.",
        shouldBlock: false,
        safeLogCode: "safety_crisis_job_search"
      };
    }
  }

  // Return corresponding results for each severity level
  if (initialSeverity === "urgent_crisis") {
    return {
      severity: "urgent_crisis",
      matchedTerms,
      message: "If you or someone else may be in danger or thinking about self-harm, please reach out. Support is available right now. Call or text 988 to reach the Suicide & Crisis Lifeline. If there is immediate danger, call emergency services now.",
      shouldBlock: true,
      safeLogCode: "safety_crisis_search_blocked"
    };
  }

  if (initialSeverity === "crisis_support") {
    return {
      severity: "crisis_support",
      matchedTerms,
      message: "If you are going through a tough time, support is available. You can call, text, or chat with the 988 Suicide & Crisis Lifeline for free, confidential, 24/7 help.",
      shouldBlock: false,
      safeLogCode: "safety_crisis_support_triggered"
    };
  }

  return {
    severity: "low_concern",
    matchedTerms,
    message: "If you are feeling overwhelmed, sad, or lonely, you don't have to carry it alone. Friendly help and supportive resources are always available.",
    shouldBlock: false,
    safeLogCode: "safety_crisis_low_concern_triggered"
  };
}
